/**
 * App lifecycle state — mirrors `installed_apps.lifecycle_state` ENUM in
 * core (Group B). The wish-declared FSM:
 *
 *   INSTALLED ──(env complete)──> DEPLOYING ──(pod healthy)──> RUNNING
 *      │                              │                            │
 *      │ (required env missing)       │ (deploy fails)              │ (env changes)
 *      ▼                              ▼                            ▼
 *   NEEDS_CONFIG <──── (key cleared / pack upgrade adds key) ─── (UI save)
 *      ▲                              │
 *      │                              ▼
 *      └─────── DEGRADED ◄──── (pod CrashLoopBackOff / ImagePullBackOff)
 *                                     │
 *                                     ▼
 *                                UNINSTALLING ──> (gone)
 */
export type LifecycleState =
	| 'INSTALLED'
	| 'NEEDS_CONFIG'
	| 'DEPLOYING'
	| 'RUNNING'
	| 'DEGRADED'
	| 'UNINSTALLING';

export const LIFECYCLE_STATES: readonly LifecycleState[] = [
	'INSTALLED',
	'NEEDS_CONFIG',
	'DEPLOYING',
	'RUNNING',
	'DEGRADED',
	'UNINSTALLING',
] as const;

/**
 * True when the lifecycle banner should be shown — i.e., the user can take
 * action OR the state is so abnormal we want to surface it. Returns false
 * for the silent-by-default states:
 *
 *   - undefined     — pre-Group-B kernel reply, no opinion → silent
 *   - RUNNING       — happy path → silent
 *   - INSTALLED     — bundled apps default to this; nothing for the user to
 *                     do unless the manifest declares required env (which
 *                     would flip the state to NEEDS_CONFIG via the kernel
 *                     reconciler). Showing a banner here yells "not yet
 *                     deployed" at every bundled-app open, which is wrong.
 *   - DEPLOYING     — transient; user can't help. A spinner in the launcher
 *                     badge is enough; don't overlay the window content.
 *
 * Banner DOES render for: NEEDS_CONFIG (the actual call-to-action),
 * DEGRADED (pod is sick), UNINSTALLING (informational, soon-to-be-gone).
 */
export function isPreReady(state: LifecycleState | undefined): boolean {
	if (state === undefined) return false;
	return state === 'NEEDS_CONFIG' || state === 'DEGRADED' || state === 'UNINSTALLING';
}
